import {
  AlertTriangle,
  ArrowUpDown,
  Atom,
  CheckCircle2,
  Clock,
  Loader2,
  Maximize2,
  Minimize2,
  Network,
  Spline,
  X,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useFlowRunDag,
  type FlowRunDagResponse,
  type FlowRunNodeState,
  type FlowRunSummary,
  type FlowRunSummaryState,
} from "@/api/flow-run-dag";
import { useRunVisuals } from "@/api/run-visuals";
import { NodeDetailBody, NodeFanoutChip } from "@/components/dag/NodeDetailSheet";
import type { TileNodeData } from "@/components/dag/TileNode";
import { EmptyState } from "@/components/layout/EmptyState";
import { formatDuration } from "@/components/runs/run-duration";
import {
  deriveRunVisualViews,
  runProgressKey,
  type RunVisualView,
} from "@/components/runs/run-visual-views";
import {
  deriveWorkspaceModes,
  resolveWorkspaceMode,
  sideHeadingFor,
  sidePanelFor,
  type RunWorkspaceMode,
  type RunWorkspaceModeId,
} from "@/components/runs/run-workspace-modes";
import { RunFragmentFits } from "@/components/runs/RunFragmentFits";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { RunSubjectMolecule, RunVisualUnavailable } from "@/components/runs/RunVisualBody";
import type { TorsionPick } from "@/components/runs/torsion-selection";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

// Lazy-loaded so reactflow (~150 KB gz) only lands when a user opens
// the DAG tab, not on every run-detail page load — same rationale +
// pattern as SubmitFlow.tsx's DAG preview. A static import here would
// pull DAGCanvas into the run-detail page's own chunk even for
// visitors who never open the tab (and would stop Rollup splitting it
// out at all, since a module both statically and dynamically imported
// bundles into the static importer's chunk).
const DAGCanvas = React.lazy(() =>
  import("@/components/dag/DAGCanvas").then((m) => ({ default: m.DAGCanvas }))
);

// Lazy for the same reason, one library down: `RunTorsionPlots` reaches
// `DihedralRMSDGrid` → `DihedralProfilePlot` → recharts (~380 KB), and this
// workspace renders on every flow run's DAG tab. Without this boundary the
// torsion view is only ever ONE of the visual panel's modes, but recharts
// would be linked into the entry chunk for every route in the app
// (flowstack#992) — including the run list, which VISUALIZATION_PLAN §4 says
// the heavy chart libraries must never touch.
const RunTorsionPlots = React.lazy(() =>
  import("@/components/runs/RunTorsionPlots").then((m) => ({ default: m.RunTorsionPlots }))
);

/**
 * The run WORKSPACE (flowstack#625, #950 Part B, #956): a run-scoped DAG,
 * the visual panel and the tile settings, plus a node status table and a
 * top summary — all projected from ONE reconstructed flow-run cohort
 * (`useFlowRunDag`) rather than the template-keyed overlay the submit
 * page uses, so no other run's stale/failed state leaks in here.
 *
 * Named for what it is as of #956. It was `FlowRunDagPanel`, which had
 * stopped being true the moment the DAG became one of three parts — and
 * the name was load-bearing, not cosmetic: while the thing that expanded
 * was "the flow graph", whether the visual came along was an open
 * question. When it is "the workspace", it obviously does.
 *
 * Renders inside the run-detail page's "DAG" tab for any run whose
 * detail carries a `flow_name` (a Flow's own root run AND its
 * per-node child runs alike — `parent_run_id` in the response tells
 * the two apart, driving the breadcrumb below the summary).
 */
export function RunWorkspace({ runId }: { runId: string }): JSX.Element {
  const query = useFlowRunDag(runId);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const notAFlowRun =
      query.error instanceof ApiError && query.error.errorCode === "not_a_flow_run";
    return (
      <EmptyState
        title={notAFlowRun ? "Not part of a Flow." : "Could not load the flow graph."}
        description={
          notAFlowRun
            ? "This is a standalone Tile run — there's no flow-run graph to show."
            : query.error instanceof Error
              ? query.error.message
              : "Unknown error."
        }
      />
    );
  }

  const { data } = query;
  const isChildRun = data.parent_run_id !== data.run_id;

  return (
    <div className="flex flex-col gap-4">
      {isChildRun && (
        <div className="rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-xs text-muted-foreground">
          Part of flow run{" "}
          <Link
            to={`/runs/${encodeURIComponent(data.parent_run_id)}`}
            className="font-mono text-foreground hover:underline"
          >
            {data.parent_run_id}
          </Link>
        </div>
      )}
      <FlowRunSummaryBanner summary={data.summary} />
      <FlowRunWorkspace data={data} />
      <FlowRunNodeTable nodes={data.nodes} />
    </div>
  );
}

/**
 * The workspace: **a main area that is a MODE, and a side panel that follows
 * it** (flowstack#991).
 *
 * The shape this replaces was a fixed split — DAG on the left always, a 26rem
 * panel on the right carrying whatever visual the run had produced (#950 Part
 * B, #956). That gave the graph the whole main area permanently and left every
 * *visual* in a column too narrow to read: three ~90px torsion cards whose
 * status labels overlapped each other, and a 3D structure smaller than the
 * settings list beneath it. Widening the column is not the fix, because the
 * column is not the problem — the DAG holding the main area unconditionally is.
 *
 * So the user picks what the main area shows (`run-workspace-modes.ts` owns the
 * table), and the side panel becomes that thing's detail: the graph pairs with
 * tile settings as before, the plots pair with the structure, and the structure
 * pairs with the fits. The side panel is never generic, which is what makes the
 * two halves read as one surface rather than two boxes that happen to be
 * adjacent — the relationship is semantic, not just visual.
 *
 * **How the width is shared.** Below `xl` (1280px) the two STACK: the main area
 * keeps the full width and the panel sits under it. `min-w-0` on the main pane
 * so reactflow's canvas can actually shrink — without it a flex child refuses
 * to go below its content width and the panel is pushed off-screen.
 *
 * **Expanded is a FIXED VIEWPORT FRAME, not taller content** (#991's first
 * comment, and most of the fix it asks for). It used to be `fixed inset-0` on
 * this row alone with the panes free to grow, so the panes got taller than the
 * viewport and THE PAGE SCROLLED — which meant the pane headers and the mode
 * controls scrolled off the top, and what was left on screen was a canvas
 * beside a column of cards with nothing common between them. That is the whole
 * of "it looks like it is not connected". Now: the frame is fixed, the
 * maximised pane's header PINS, the pane scrolls INTERNALLY, and the page
 * itself never scrolls.
 *
 * **EACH PANE MAXIMISES ON ITS OWN (VIZ U5.3).** The frame used to take the
 * whole workspace: one `expanded` boolean, one control, both panes side by
 * side inside it. That is the wrong unit for the thing a user is actually
 * doing — reading a fifteen-node graph, or a dihedral grid, or a structure —
 * because whichever one they came for still only got a share of the window,
 * and in the frame the share was FIXED (the aside's 26rem was no longer
 * negotiable at any width). So the state is now *which pane is maximised*,
 * `null | "main" | "side"`, and each pane carries its own control.
 *
 * A three-valued state rather than two booleans, deliberately: two booleans
 * admit "both maximised", which has no meaning in a single `fixed inset-0`
 * frame, and a type that cannot express the impossible state is worth more
 * than a runtime guard against it (the same reasoning as `MAIN_SCROLLS` and
 * `SIDE_BY_MODE` being exhaustive `Record`s rather than switches).
 *
 * **The sibling pane is HIDDEN, never unmounted.** `hidden` (`display: none`)
 * keeps its React state, its react-query cache subscriptions and — the case
 * that matters — a `MoleculeViewer`'s live WebGL context alive, so restoring
 * comes back to the plot/structure the user left rather than to a reset one.
 * Unmounting would be simpler and is exactly the bug U5.3 forbids.
 *
 * **`min-h-0` is load-bearing, not tidiness.** A flex child defaults to
 * `min-height: auto` and refuses to shrink below its content, which breaks both
 * `h-full` on the nested canvas and the internal scroll this frame depends on —
 * the exact failure `AppShell.tsx` carries a warning about (PR-334 r2 #1,
 * re-verified in #982). Per-pane maximise creates TWO expanded paths where
 * there was one, and every flex child of each carries it: frame → row →
 * the maximised `Card` → that card's body → the content's own fill wrapper.
 */
function FlowRunWorkspace({ data }: { data: FlowRunDagResponse }): JSX.Element {
  const [selected, setSelected] = React.useState<TileNodeData | null>(null);
  const [pickedMode, setPickedMode] = React.useState<RunWorkspaceModeId | null>(null);
  const [pickedFragment, setPickedFragment] = React.useState<string | null>(null);
  // The INNER axis of the same selection (VIZ U5.1). Held here for the reason
  // the fragment is: the plots show one torsion and the side panel names it,
  // so a single answer has to serve both. Stored as the RAW pick — each reader
  // resolves it against the fragment's current torsion list
  // (`resolveTorsionId`), which is what keeps a fragment change from carrying a
  // torsion id (or an index) into a fragment that has no such torsion. The pick
  // carries the fragment it was made in, because a torsion_id is unique only
  // within one fragment (`TorsionPick`).
  const [pickedTorsion, setPickedTorsion] = React.useState<TorsionPick | null>(null);
  // WHICH pane is maximised — not two booleans (see the component docstring).
  const [maximised, setMaximised] = React.useState<MaximisedPane | null>(null);

  // The run's own progress signal, reused: `useFlowRunDag` already polls
  // every 5s while the summary is active and stops when it is terminal, and
  // every response carries each node's status. Folding those into a key is
  // what makes the visual surfaces progressive WITHOUT a second polling loop.
  const progressKey = runProgressKey(data.nodes);
  const runIsLive = LIVE_SUMMARY_STATES.has(data.summary.state);
  // Keeps the canvas's layout memo from recomputing on every render of
  // this component (selecting a node re-renders it). The canvas no
  // longer keys its stale-selection clear on this object's identity —
  // that was the PR-955 r0 defect, since a 5s poll changes the
  // reference on exactly the progress events the surface exists to show
  // — so this is now an optimisation rather than a correctness guard.
  const dag = React.useMemo(
    () => ({ flow_name: data.flow_name, nodes: data.nodes, edges: data.edges }),
    [data.flow_name, data.nodes, data.edges]
  );

  // The visuals index moved UP here from the old panel: the mode switch is
  // what needs to know which views a run can offer, and both panes now render
  // from the same answer. One query, deduped by TanStack for any component
  // that asks again with the same key.
  const visuals = useRunVisuals(data.run_id, progressKey);
  const views = deriveRunVisualViews({
    visuals: visuals.data,
    runIsLive,
    selectedNodeName: selected?.name ?? null,
  });
  const modes = deriveWorkspaceModes(views);
  const mode = resolveWorkspaceMode(modes, pickedMode);
  const side = sidePanelFor(mode.id);
  const moleculeView = views.find((v) => v.id === "molecule") ?? null;

  // Each pane's own name, used in FOUR places that must agree: the card's
  // `aria-label`, its control's `aria-label`/`title`, and the frame's label
  // saying which pane you are in. Derived once so they cannot drift.
  const mainName = `${mode.label} view`;
  const sideName = `${sideHeadingFor(side)} side panel`;
  const mainMaximised = maximised === "main";
  const sideMaximised = maximised === "side";
  const framed = maximised !== null;

  // The frame covers the page but the page keeps its scrollbar, and a wheel
  // event reactflow does not take would scroll the document behind it.
  React.useEffect(() => {
    if (!framed) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [framed]);

  // Escape exits the expanded frame — owned HERE as of #991, because the
  // control it mirrors moved here. It used to live inside `DAGCanvas`, which
  // works only while the graph is on screen; with a mode switch the canvas is
  // unmounted in two of three modes, so an Escape handler inside it would
  // silently stop existing exactly when a user is deepest in a full-screen
  // view. No sheet-first guard is needed at this level: the node detail
  // renders in the side panel here, never as a radix dialog (see
  // `DAGCanvasProps.onNodeSelect`), so there is nothing for Escape to close
  // first.
  //
  // It closes WHICHEVER pane is maximised, because there is only ever one
  // (U5.3) — and it still cannot fight `DAGCanvas`'s own key handling: the
  // canvas registers an Escape listener only when `fullscreen` is
  // `undefined`, and this workspace always passes a boolean, in BOTH pane
  // states and while the canvas sits hidden behind a maximised side panel.
  React.useEffect(() => {
    if (!framed) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setMaximised(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [framed]);

  return (
    <div
      className={cn(
        "flex flex-col",
        // The fixed frame. `flex-col` + `min-h-0 flex-1` on the row below is
        // what confines the pane to the viewport instead of letting it grow
        // the page.
        framed && "fixed inset-0 z-50 bg-background p-3"
      )}
      role={framed ? "region" : undefined}
      // Names WHICH pane is expanded, not just that something is: with two
      // maximisable panes "workspace, expanded" left a screen-reader user
      // unable to tell what they had just entered.
      aria-label={
        framed
          ? `${data.flow_name} workspace, ${mainMaximised ? mainName : sideName} expanded`
          : undefined
      }
    >
      <div
        className={cn(
          "flex flex-col gap-4 xl:flex-row xl:items-stretch",
          // Expanded: a row at EVERY width — the stacked arrangement exists to
          // protect a narrow *page*, and a full-screen frame is not one — and
          // `min-h-0` so the maximised pane can shrink inside the frame.
          framed && "min-h-0 flex-1 flex-row"
        )}
      >
        <Card
          as="section"
          className={cn(
            "flex min-w-0 flex-1 flex-col overflow-hidden",
            mainMaximised && "min-h-0",
            // HIDDEN, not unmounted — the canvas keeps its viewport and the
            // plots keep their selection while the side panel has the frame.
            sideMaximised && "hidden"
          )}
          aria-label={mainName}
        >
          {/* PINNED header. `shrink-0` is what keeps it on screen while the
              body below scrolls — the thing that used to scroll away.
              `panel-head` is the hairline's wash (`globals.css`). */}
          <div className="panel-head flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
            {/* The mode switch as a segmented control: ONE track cut a step
                into the panel (`bg-panel-well`), with the active segment
                raised back out of it to panel level. The loose pills this
                replaces gave the active mode no ground to be raised from, so
                "which view am I in" rested entirely on a border-and-ink
                difference between three free-floating chips.

                SEMANTICS ARE DELIBERATELY UNCHANGED — still a `role="group"`
                of `aria-pressed` buttons, not a radiogroup, and the dispatch
                asked for the reasoning rather than a default. A radiogroup is
                the right pattern when the options are a *value being chosen*
                and the group is one tab stop with arrow-key roving focus. This
                control is neither: each button acts on press (there is no
                committed selection separate from the act), and modes can be
                individually DISABLED when a run has not produced that visual
                yet — a state radio semantics handles badly and roving focus
                handles worse, since the disabled member is the one focus must
                skip. Adopting it would also have meant writing the key
                handling, which is behaviour, and this unit is chrome. */}
            <div
              className="flex flex-wrap items-center gap-0.5 rounded-lg border border-border-subtle bg-panel-well p-[3px]"
              role="group"
              aria-label="View"
            >
              {modes.map((m) => (
                <ModeButton
                  key={m.id}
                  mode={m}
                  active={m.id === mode.id}
                  onSelect={() => setPickedMode(m.id)}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {/* The pin is only visible in the DAG's own side panel, so in a
                  visual mode it needs saying here — otherwise a structure
                  silently pinned to a step reads as the run's ligand, which is
                  the unverifiable render the fragment-vs-ligand rule exists to
                  prevent. */}
              {selected !== null && mode.id !== "dag" && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label={`Clear the selected step ${selected.name}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  <span className="font-mono">{selected.name}</span>
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
              <MaximiseButton
                paneName={mainName}
                maximised={mainMaximised}
                onToggle={() => setMaximised(mainMaximised ? null : "main")}
              />
            </div>
          </div>
          <div
            className={cn(
              "flex flex-col p-2",
              mainMaximised && "min-h-0 flex-1",
              // Only the plots scroll: the graph and the 3D viewer FILL their
              // box (a scrollbar on a pannable canvas is a trap), while a
              // fragment's dihedral grid is a list that can exceed any frame.
              mainMaximised && (MAIN_SCROLLS[mode.id] ? "overflow-y-auto" : "overflow-hidden")
            )}
          >
            <WorkspaceMain
              mode={mode}
              dag={dag}
              runId={data.run_id}
              progressKey={progressKey}
              runIsLive={runIsLive}
              maximised={mainMaximised}
              selectedNodeName={selected?.name ?? null}
              onNodeSelect={setSelected}
              selectedFragmentId={pickedFragment}
              onFragmentSelect={setPickedFragment}
              selectedTorsion={pickedTorsion}
              onTorsionSelect={setPickedTorsion}
            />
          </div>
        </Card>

        <Card
          as="aside"
          className={cn(
            "flex flex-col overflow-hidden",
            // Maximised the aside is the FRAME, so it drops the 26rem column
            // width it wears beside the main pane — a detail panel pinned to
            // 26rem inside a full-screen frame would be the narrow column
            // #991 set out to abolish, just with more empty space beside it.
            sideMaximised ? "min-h-0 w-full flex-1" : "w-full xl:w-[26rem] xl:shrink-0",
            mainMaximised && "hidden"
          )}
          aria-label={`${sideHeadingFor(side)} panel`}
        >
          <div className="panel-head flex shrink-0 items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
            <h2 className="label-micro truncate text-muted-foreground">{sideHeadingFor(side)}</h2>
            <div className="flex items-center gap-1.5">
              {side === "tile" && selected !== null && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label="Clear the selected step"
                  className="rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
              <MaximiseButton
                paneName={sideName}
                maximised={sideMaximised}
                onToggle={() => setMaximised(sideMaximised ? null : "side")}
              />
            </div>
          </div>
          <div
            className={cn("flex flex-col p-3", sideMaximised && "min-h-0 flex-1 overflow-y-auto")}
          >
            <WorkspaceSide
              side={side}
              node={selected}
              flowName={data.flow_name}
              runId={data.run_id}
              progressKey={progressKey}
              runIsLive={runIsLive}
              moleculeView={moleculeView}
              maximised={sideMaximised}
              selectedFragmentId={pickedFragment}
              onFragmentSelect={setPickedFragment}
              selectedTorsion={pickedTorsion}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * Which of the workspace's two panes has the frame (VIZ U5.3).
 *
 * A closed three-valued state — `null | "main" | "side"` — rather than one
 * boolean per pane. There is exactly one `fixed inset-0` frame, so "both
 * maximised" is not a state the UI has; expressing it as two booleans would
 * make it representable and then need a runtime guard to keep it from
 * happening. This makes it unspellable instead.
 */
type MaximisedPane = "main" | "side";

/**
 * A pane's own maximise control.
 *
 * ONE component for both panes so the two cannot drift into different chrome
 * or different wording, and so the accessible name is always built the same
 * way: the verb plus the pane's name. "Expand workspace" was unambiguous while
 * there was one frame for the whole workspace; with two, a control that does
 * not say WHAT it expands is a control the user has to click to find out.
 *
 * The glyph is `aria-hidden`, so the accessible name is exactly `paneName`
 * with its verb — the icon is a second cue, never the only one.
 */
function MaximiseButton({
  paneName,
  maximised,
  onToggle,
}: {
  paneName: string;
  maximised: boolean;
  onToggle: () => void;
}): JSX.Element {
  const label = maximised ? `Exit the expanded ${paneName}` : `Expand the ${paneName}`;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={maximised ? `${label} (Esc)` : label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-surface-2 text-muted-foreground ring-offset-background transition-colors hover:bg-surface-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {maximised ? (
        <Minimize2 className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Maximize2 className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

/**
 * Whether the main area's body scrolls internally when the MAIN pane is
 * maximised.
 *
 * A `Record`, so a mode added without deciding this is a type error rather
 * than a silent default.
 */
const MAIN_SCROLLS: Record<RunWorkspaceModeId, boolean> = {
  dag: false,
  molecule: false,
  torsion: true,
};

/** The main area — one of three things, chosen by the mode. */
function WorkspaceMain({
  mode,
  dag,
  runId,
  progressKey,
  runIsLive,
  maximised,
  selectedNodeName,
  onNodeSelect,
  selectedFragmentId,
  onFragmentSelect,
  selectedTorsion,
  onTorsionSelect,
}: {
  mode: RunWorkspaceMode;
  dag: Pick<FlowRunDagResponse, "flow_name" | "nodes" | "edges">;
  runId: string;
  progressKey: string;
  runIsLive: boolean;
  /** THIS pane has the frame — never merely "the workspace is framed". */
  maximised: boolean;
  selectedNodeName: string | null;
  onNodeSelect: (node: TileNodeData | null) => void;
  selectedFragmentId: string | null;
  onFragmentSelect: (fragmentId: string) => void;
  selectedTorsion: TorsionPick | null;
  onTorsionSelect: (pick: TorsionPick) => void;
}): JSX.Element {
  if (mode.id === "dag") {
    return (
      <React.Suspense fallback={<Skeleton className="h-[360px] w-full" />}>
        <DAGCanvas
          dag={dag}
          onNodeSelect={onNodeSelect}
          selectedNodeName={selectedNodeName}
          // Defined unconditionally, so the canvas never renders a toggle of
          // its own (nor an Escape handler): the control lives in the pane
          // header now, where it survives a switch away from the graph. See
          // `DAGCanvasProps`.
          //
          // It is the MAIN pane's own state, not "something is expanded" —
          // that is what re-fits the graph when the graph's pane takes the
          // frame, and leaves the viewport alone when the SIDE panel does
          // (`useAutoFitView` keys on this flag beside the measured size).
          fullscreen={maximised}
        />
      </React.Suspense>
    );
  }
  // `resolveWorkspaceMode` only returns a `ready` visual mode, so this is a
  // guard against a mode going stale between render and paint rather than an
  // expected path — but it must say WHICH of pending/absent it is, never draw
  // an empty frame (flowstack#923).
  if (mode.view === null || mode.view.availability !== "ready") {
    return mode.view === null ? (
      <EmptyState title="Nothing to show." description="This view has no data." />
    ) : (
      <RunVisualUnavailable view={mode.view} runIsLive={runIsLive} />
    );
  }
  if (mode.id === "torsion") {
    return (
      <React.Suspense fallback={<Skeleton className="h-48 w-full" />}>
        <RunTorsionPlots
          runId={runId}
          progressKey={progressKey}
          selectedFragmentId={selectedFragmentId}
          onFragmentSelect={onFragmentSelect}
          selectedTorsion={selectedTorsion}
          onTorsionSelect={onTorsionSelect}
        />
      </React.Suspense>
    );
  }
  return (
    <RunSubjectMolecule
      contentHash={mode.view.artifact?.content_hash ?? null}
      // The main area IS the frame here, so the viewer takes all of it.
      fill={maximised}
      siblingCount={mode.view.siblingInputCount ?? 0}
      heading={mode.view.pinnedToNode ? `Input to ${mode.view.pinnedToNode}` : "Molecule"}
    />
  );
}

/** The side panel — the DETAIL of whatever the main area is showing. */
function WorkspaceSide({
  side,
  node,
  flowName,
  runId,
  progressKey,
  runIsLive,
  moleculeView,
  maximised,
  selectedFragmentId,
  onFragmentSelect,
  selectedTorsion,
}: {
  side: ReturnType<typeof sidePanelFor>;
  node: TileNodeData | null;
  flowName: string;
  runId: string;
  progressKey: string;
  runIsLive: boolean;
  moleculeView: RunVisualView | null;
  /** THIS pane has the frame — never merely "the workspace is framed". */
  maximised: boolean;
  selectedFragmentId: string | null;
  onFragmentSelect: (fragmentId: string) => void;
  /** The workspace's raw torsion pick — the side panel resolves it itself, so
   * it names the torsion the plots are actually showing. */
  selectedTorsion: TorsionPick | null;
}): JSX.Element {
  if (side === "tile") {
    return <NodeInfoBody node={node} flowName={flowName} />;
  }
  if (side === "molecule") {
    // Reading a fit: the structure is the reference beside the curves, and
    // the selected fragment's own numbers sit under it — so the panel is
    // about THIS plot rather than about the run in general.
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {moleculeView === null || moleculeView.availability !== "ready" ? (
          moleculeView === null ? (
            <EmptyState
              title="No molecule to show."
              description="This run has no structure-bearing artifact to draw beside the plots."
            />
          ) : (
            <RunVisualUnavailable view={moleculeView} runIsLive={runIsLive} />
          )
        ) : (
          <RunSubjectMolecule
            contentHash={moleculeView.artifact?.content_hash ?? null}
            fill={maximised}
            siblingCount={moleculeView.siblingInputCount ?? 0}
            heading={
              moleculeView.pinnedToNode ? `Input to ${moleculeView.pinnedToNode}` : "Molecule"
            }
          />
        )}
        <RunFragmentFits
          runId={runId}
          progressKey={progressKey}
          runIsLive={runIsLive}
          selectedFragmentId={selectedFragmentId}
          onFragmentSelect={onFragmentSelect}
          // The plots' pairing: this panel is the detail of ONE torsion now, so
          // it names that torsion as well as its fragment (VIZ U5.1).
          selectedTorsion={selectedTorsion}
          showList={false}
        />
      </div>
    );
  }
  // ...and inverted: reading a structure, the fits are the reference. The
  // list is selectable, so picking a fragment here is the same selection the
  // plots use when the user switches to them.
  return (
    <RunFragmentFits
      runId={runId}
      progressKey={progressKey}
      runIsLive={runIsLive}
      selectedFragmentId={selectedFragmentId}
      onFragmentSelect={onFragmentSelect}
      showList
    />
  );
}

/**
 * The tile settings — what the overlay sheet used to carry.
 *
 * Renders `NodeDetailBody`, the same component the sheet renders, so the two
 * surfaces cannot drift. Only the heading chrome differs, and that now lives
 * in the side pane's pinned header.
 */
function NodeInfoBody({
  node,
  flowName,
}: {
  node: TileNodeData | null;
  flowName: string;
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="font-display text-base">{node?.name ?? "Step"}</h3>
          {node?.fanout_kind && <NodeFanoutChip fanoutKind={node.fanout_kind} />}
        </div>
        <span className="truncate font-mono text-xs text-muted-foreground">
          {node?.tile ?? "—"}
        </span>
      </div>
      <NodeDetailBody
        nodeName={node?.name ?? null}
        tileName={node?.tile ?? null}
        fanoutKind={node?.fanout_kind ?? null}
        status={node?.status ?? null}
        runId={node?.run_id ?? null}
        flowName={flowName}
      />
    </div>
  );
}

/**
 * The glyph each mode wears in the switch.
 *
 * A `Record` over the mode ids for the same reason `MAIN_SCROLLS` is one: a
 * mode added without deciding its icon is a type error, not a segment that
 * silently renders as a bare label beside two that do not. It lives here
 * rather than on `RunWorkspaceMode` deliberately — `run-workspace-modes.ts`
 * is a pure module about which view resolves and which panel pairs with it,
 * and an icon component is presentation this file already owns.
 *
 * They are a SECOND cue, never the only one: every segment keeps its text
 * label beside the glyph, and the icon is `aria-hidden` so the accessible
 * name is still exactly the label.
 */
const MODE_ICONS: Record<RunWorkspaceModeId, LucideIcon> = {
  dag: Network,
  molecule: Atom,
  torsion: Spline,
};

/**
 * One segment of the switch.
 *
 * A mode whose data does not exist yet is offered-but-disabled and says
 * *pending*, with the reason in its title — never hidden (which reads as "the
 * portal cannot do that") and never enabled-then-empty (which reads as a bug).
 * Carried over verbatim from the old visual selector; the three-state rule is
 * `run-visual-views.ts`'s, not restated here.
 *
 * The ACTIVE state carries three cues, and the two non-chromatic ones are
 * load-bearing — the same rule the rail's active row follows. The segment is
 * raised out of the well back to panel level (`bg-card`), which is a
 * measurable lightness step rather than a hue judgement; `shadow-sm` puts the
 * theme's own lift on it (a drop in light, a 1px top highlight in dark); and
 * its ink goes to `--foreground` from `--muted-foreground`. An inactive
 * segment paints NO background at all — the well behind it is the ground — so
 * exactly one segment in the group is ever a filled shape.
 */
function ModeButton({
  mode,
  active,
  onSelect,
}: {
  mode: RunWorkspaceMode;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const ready = mode.availability === "ready";
  const Icon = MODE_ICONS[mode.id];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!ready}
      aria-pressed={active}
      title={
        ready
          ? undefined
          : mode.availability === "pending"
            ? `${mode.label} — not produced yet`
            : `${mode.label} — this run produced none`
      }
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors",
        active && ready ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
        ready ? "hover:text-foreground" : "cursor-not-allowed opacity-60"
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {mode.label}
      {mode.availability === "pending" && (
        <span className="text-[9px] uppercase tracking-wider">pending</span>
      )}
    </button>
  );
}

/** Summary states from which the flow could still produce something new.
 * Mirrors `POLLING_STATES` in `@/api/flow-run-dag` — and for the same
 * reason: `not_started` is also the state right after submit, before the
 * first row lands, so it is genuinely live. */
const LIVE_SUMMARY_STATES: ReadonlySet<FlowRunSummaryState> = new Set([
  "not_started",
  "running",
  "blocked",
]);

const SUMMARY_META: Record<
  FlowRunSummaryState,
  { icon: React.ComponentType<{ className?: string }>; classes: string }
> = {
  failed: {
    icon: AlertTriangle,
    classes:
      "border-state-failed-system-border bg-state-failed-system-bg text-state-failed-system-fg",
  },
  running: {
    icon: Loader2,
    classes: "border-state-running-border bg-state-running-bg text-state-running-fg",
  },
  blocked: {
    icon: Clock,
    classes: "border-state-staging-border bg-state-staging-bg text-state-staging-fg",
  },
  completed: {
    icon: CheckCircle2,
    classes: "border-state-completed-border bg-state-completed-bg text-state-completed-fg",
  },
  not_started: {
    icon: Clock,
    classes: "border-border-subtle bg-surface-2 text-muted-foreground",
  },
};

/** Top-of-page "running / blocked at / failed at <node>" headline. */
function FlowRunSummaryBanner({ summary }: { summary: FlowRunSummary }): JSX.Element {
  const meta = SUMMARY_META[summary.state];
  const Icon = meta.icon;
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-lg border px-4 py-3 text-sm font-medium",
        meta.classes
      )}
    >
      <Icon
        className={cn("h-4 w-4 shrink-0", summary.state === "running" && "animate-spin")}
        aria-hidden
      />
      <span>{summary.message}</span>
    </div>
  );
}

type SortKey = "name" | "status" | "duration_seconds";
type SortDir = "asc" | "desc";

const TOPOLOGY_LABEL: Record<"scope" | "match", string> = {
  // Human-friendly names for the internal SCOPE/MATCH fanout kinds —
  // UI-only, the YAML keywords (`over`/`as`, `match`) are unchanged.
  scope: "Scatter",
  match: "Aligned",
};

/** Node status table — node / tile / topology / state / duration /
 * cache / child run, sortable by clicking a header. */
function FlowRunNodeTable({ nodes }: { nodes: FlowRunNodeState[] }): JSX.Element {
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({
    key: "name",
    dir: "asc",
  });

  const toggleSort = (key: SortKey): void => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const sorted = React.useMemo(() => {
    const withIndex = nodes.map((node, index) => ({ node, index }));
    withIndex.sort((a, b) => {
      const cmp = compareBy(sort.key, a.node, b.node);
      if (cmp !== 0) return sort.dir === "asc" ? cmp : -cmp;
      return a.index - b.index; // stable fallback — declaration order
    });
    return withIndex.map((x) => x.node);
  }, [nodes, sort]);

  return (
    <Card className="p-2">
      <Table role="table">
        <TableCaption>Per-node status for this flow run</TableCaption>
        <TableHeader>
          <TableRow>
            <SortableHeader label="Node" sortKey="name" sort={sort} onSort={toggleSort} />
            <TableHead>Tile</TableHead>
            <TableHead>Topology</TableHead>
            <SortableHeader label="State" sortKey="status" sort={sort} onSort={toggleSort} />
            <SortableHeader
              label="Duration"
              sortKey="duration_seconds"
              sort={sort}
              onSort={toggleSort}
            />
            <TableHead>Cache</TableHead>
            <TableHead>Child run</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((node) => (
            <TableRow key={node.name} interactive="hover">
              <TableCell className="font-mono text-xs">{node.name}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{node.tile}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {node.fanout_kind ? TOPOLOGY_LABEL[node.fanout_kind] : "—"}
              </TableCell>
              <TableCell>
                {node.status ? (
                  <div className="flex items-center gap-1.5">
                    <RunStateBadge state={node.status} />
                    {node.child_count > 1 && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        ×{node.child_count}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">not started</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs tabular-nums">
                {formatDuration(node.duration_seconds)}
              </TableCell>
              <TableCell className="text-xs">
                {node.cache_hit === null ? "—" : node.cache_hit ? "hit" : "miss"}
              </TableCell>
              <TableCell className="text-xs">
                {node.child_count === 1 && node.run_id ? (
                  <Link
                    to={`/runs/${encodeURIComponent(node.run_id)}`}
                    className="text-foreground hover:underline"
                  >
                    View run →
                  </Link>
                ) : node.child_count > 1 ? (
                  <span className="text-muted-foreground">
                    {node.child_count.toLocaleString()} runs
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function compareBy(key: SortKey, a: FlowRunNodeState, b: FlowRunNodeState): number {
  if (key === "name") return a.name.localeCompare(b.name);
  if (key === "status") return (a.status ?? "").localeCompare(b.status ?? "");
  // duration_seconds — nulls sort last regardless of direction, since
  // "no duration yet" isn't meaningfully smaller or larger than a
  // real value.
  const av = a.duration_seconds;
  const bv = b.duration_seconds;
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av - bv;
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}): JSX.Element {
  const active = sort.key === sortKey;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground",
          active && "text-foreground"
        )}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <ArrowUpDown className={cn("h-3 w-3", active ? "opacity-100" : "opacity-40")} aria-hidden />
      </button>
    </TableHead>
  );
}
